import React, { useState } from 'react';
import './Checkout.css';
import { useCart } from '../../context/CartContext';
import Button from '../UI/Button';

const Checkout = ({ setRoute }) => {
  const { cart, getCartTotal, getCartItemCount, clearCart } = useCart();
  
  // Form state
  const [formData, setFormData] = useState({
    // Customer Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Billing Address
    billingAddress: '',
    billingAddress2: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: 'US',
    
    // Shipping Address
    sameAsBilling: true,
    shippingAddress: '',
    shippingAddress2: '',
    shippingCity: '',
    shippingState: '',
    shippingZip: '',
    shippingCountry: 'US',
    
    // Payment & Order Notes
    paymentMethod: 'card',
    orderNotes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate totals
  const subtotal = getCartTotal();
  const shipping = subtotal > 50 ? 0 : 8.99; // Free shipping over $50
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + shipping + tax;

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Limit to 10 digits
    const limitedNumber = phoneNumber.slice(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (limitedNumber.length <= 3) {
      return limitedNumber;
    } else if (limitedNumber.length <= 6) {
      return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
    } else {
      return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Format phone number input
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    const required = ['firstName', 'lastName', 'email', 'phone', 'billingAddress', 'billingCity', 'billingState', 'billingZip'];
    
    required.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation - check for 10 digits in formatted phone
    const phoneDigits = formData.phone.replace(/[^\d]/g, '');
    if (formData.phone && phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    // Shipping address validation if different from billing
    if (!formData.sameAsBilling) {
      ['shippingAddress', 'shippingCity', 'shippingState', 'shippingZip'].forEach(field => {
        if (!formData[field].trim()) {
          newErrors[field] = 'This field is required';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (cart.length === 0) {
      alert('Your cart is empty!');
      return;
    }

    setIsSubmitting(true);

    // Process order through Azure Functions API
    try {
      // Create order data
      const orderData = {
        orderId: `KK-${Date.now()}`,
        customer: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        billing: {
          address: formData.billingAddress,
          address2: formData.billingAddress2,
          city: formData.billingCity,
          state: formData.billingState,
          zip: formData.billingZip,
          country: formData.billingCountry
        },
        shipping: formData.sameAsBilling ? null : {
          address: formData.shippingAddress,
          address2: formData.shippingAddress2,
          city: formData.shippingCity,
          state: formData.shippingState,
          zip: formData.shippingZip,
          country: formData.shippingCountry
        },
        items: cart,
        totals: {
          subtotal,
          shipping,
          tax,
          total
        },
        paymentMethod: formData.paymentMethod,
        orderNotes: formData.orderNotes,
        timestamp: new Date().toISOString()
      };

      // Submit order to Azure Functions API
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (result.success) {
        // Store order in localStorage for reference
        localStorage.setItem('lastOrder', JSON.stringify(orderData));
        
        // Clear cart
        clearCart();
        
        // Show success message
        alert(`Order ${orderData.orderId} placed successfully!\n\nTotal: $${total.toFixed(2)}\n\nConfirmation emails have been sent.\n\nThank you for your order!`);
        
        // Navigate back to home
        setRoute('home');
      } else {
        throw new Error(result.error || 'Failed to process order');
      }
      
    } catch (error) {
      console.error('Order submission error:', error);
      alert('There was an error processing your order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <section className="checkout-section">
        <div className="container">
          <div className="checkout-empty">
            <i className="fas fa-shopping-cart empty-cart-icon"></i>
            <h2>Your cart is empty</h2>
            <p>Add some delicious Kajun seasonings to your cart before checking out!</p>
            <Button 
              variant="primary" 
              onClick={() => setRoute('products')}
            >
              Shop Our Seasonings
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="checkout-section">
      <div className="container">
        <div className="checkout-header">
          <button 
            className="back-btn"
            onClick={() => setRoute('home')}
            aria-label="Go back"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1>Secure Checkout</h1>
          <div className="checkout-security">
            <i className="fas fa-shield-alt"></i>
            <span>SSL Secured</span>
          </div>
        </div>

        <div className="checkout-grid">
          {/* Checkout Form */}
          <div className="checkout-form-card">
            <form onSubmit={handleSubmit}>
              {/* Customer Information */}
              <div className="form-section customer-info-section">
                <h3>Customer Information</h3>
                <div className="form-grid customer-info-grid">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={errors.firstName ? 'error' : ''}
                      placeholder="John"
                      required
                    />
                    {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={errors.lastName ? 'error' : ''}
                      placeholder="Smith"
                      required
                    />
                    {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="john.smith@example.com"
                      required
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={errors.phone ? 'error' : ''}
                      placeholder="(555) 123-4567"
                      required
                    />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="form-section">
                <h3>Billing Address</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="billingAddress">Address *</label>
                    <input
                      type="text"
                      id="billingAddress"
                      name="billingAddress"
                      value={formData.billingAddress}
                      onChange={handleInputChange}
                      className={errors.billingAddress ? 'error' : ''}
                      placeholder="123 Main Street"
                      required
                    />
                    {errors.billingAddress && <span className="error-text">{errors.billingAddress}</span>}
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="billingAddress2">Apartment, Suite, Etc. (Optional)</label>
                    <input
                      type="text"
                      id="billingAddress2"
                      name="billingAddress2"
                      value={formData.billingAddress2}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="billingCity">City *</label>
                    <input
                      type="text"
                      id="billingCity"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleInputChange}
                      className={errors.billingCity ? 'error' : ''}
                      placeholder="New Orleans"
                      required
                    />
                    {errors.billingCity && <span className="error-text">{errors.billingCity}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="billingState">State *</label>
                    <select
                      id="billingState"
                      name="billingState"
                      value={formData.billingState}
                      onChange={handleInputChange}
                      className={errors.billingState ? 'error' : ''}
                      required
                    >
                      <option value="">Select State</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </select>
                    {errors.billingState && <span className="error-text">{errors.billingState}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="billingZip">ZIP Code *</label>
                    <input
                      type="text"
                      id="billingZip"
                      name="billingZip"
                      value={formData.billingZip}
                      onChange={handleInputChange}
                      className={errors.billingZip ? 'error' : ''}
                      placeholder="70112"
                      required
                    />
                    {errors.billingZip && <span className="error-text">{errors.billingZip}</span>}
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="form-section">
                <h3>Shipping Address</h3>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="sameAsBilling"
                      checked={formData.sameAsBilling}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    Same as billing address
                  </label>
                </div>

                {!formData.sameAsBilling && (
                  <div className="form-grid">
                    <div className="form-group full-width">
                      <label htmlFor="shippingAddress">Address *</label>
                      <input
                        type="text"
                        id="shippingAddress"
                        name="shippingAddress"
                        value={formData.shippingAddress}
                        onChange={handleInputChange}
                        className={errors.shippingAddress ? 'error' : ''}
                        required={!formData.sameAsBilling}
                      />
                      {errors.shippingAddress && <span className="error-text">{errors.shippingAddress}</span>}
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor="shippingAddress2">Apartment, Suite, Etc. (Optional)</label>
                      <input
                        type="text"
                        id="shippingAddress2"
                        name="shippingAddress2"
                        value={formData.shippingAddress2}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="shippingCity">City *</label>
                      <input
                        type="text"
                        id="shippingCity"
                        name="shippingCity"
                        value={formData.shippingCity}
                        onChange={handleInputChange}
                        className={errors.shippingCity ? 'error' : ''}
                        required={!formData.sameAsBilling}
                      />
                      {errors.shippingCity && <span className="error-text">{errors.shippingCity}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="shippingState">State *</label>
                      <select
                        id="shippingState"
                        name="shippingState"
                        value={formData.shippingState}
                        onChange={handleInputChange}
                        className={errors.shippingState ? 'error' : ''}
                        required={!formData.sameAsBilling}
                      >
                        <option value="">Select State</option>
                        <option value="AL">Alabama</option>
                        <option value="AK">Alaska</option>
                        <option value="AZ">Arizona</option>
                        <option value="AR">Arkansas</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="DE">Delaware</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="HI">Hawaii</option>
                        <option value="ID">Idaho</option>
                        <option value="IL">Illinois</option>
                        <option value="IN">Indiana</option>
                        <option value="IA">Iowa</option>
                        <option value="KS">Kansas</option>
                        <option value="KY">Kentucky</option>
                        <option value="LA">Louisiana</option>
                        <option value="ME">Maine</option>
                        <option value="MD">Maryland</option>
                        <option value="MA">Massachusetts</option>
                        <option value="MI">Michigan</option>
                        <option value="MN">Minnesota</option>
                        <option value="MS">Mississippi</option>
                        <option value="MO">Missouri</option>
                        <option value="MT">Montana</option>
                        <option value="NE">Nebraska</option>
                        <option value="NV">Nevada</option>
                        <option value="NH">New Hampshire</option>
                        <option value="NJ">New Jersey</option>
                        <option value="NM">New Mexico</option>
                        <option value="NY">New York</option>
                        <option value="NC">North Carolina</option>
                        <option value="ND">North Dakota</option>
                        <option value="OH">Ohio</option>
                        <option value="OK">Oklahoma</option>
                        <option value="OR">Oregon</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="RI">Rhode Island</option>
                        <option value="SC">South Carolina</option>
                        <option value="SD">South Dakota</option>
                        <option value="TN">Tennessee</option>
                        <option value="TX">Texas</option>
                        <option value="UT">Utah</option>
                        <option value="VT">Vermont</option>
                        <option value="VA">Virginia</option>
                        <option value="WA">Washington</option>
                        <option value="WV">West Virginia</option>
                        <option value="WI">Wisconsin</option>
                        <option value="WY">Wyoming</option>
                      </select>
                      {errors.shippingState && <span className="error-text">{errors.shippingState}</span>}
                    </div>

                    <div className="form-group">
                      <label htmlFor="shippingZip">ZIP Code *</label>
                      <input
                        type="text"
                        id="shippingZip"
                        name="shippingZip"
                        value={formData.shippingZip}
                        onChange={handleInputChange}
                        className={errors.shippingZip ? 'error' : ''}
                        required={!formData.sameAsBilling}
                      />
                      {errors.shippingZip && <span className="error-text">{errors.shippingZip}</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment & Order Notes */}
              <div className="form-section">
                <h3>Payment & Notes</h3>
                <div className="form-group">
                  <label htmlFor="paymentMethod">Payment Method</label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                  >
                    <option value="card">Credit/Debit Card</option>
                    <option value="paypal">PayPal</option>
                    <option value="apple-pay">Apple Pay</option>
                    <option value="google-pay">Google Pay</option>
                    <option value="zelle">Zelle</option>
                    <option value="venmo">Venmo</option>
                    <option value="cashapp">CashApp</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="orderNotes">Order Notes (optional)</label>
                  <textarea
                    id="orderNotes"
                    name="orderNotes"
                    value={formData.orderNotes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Any special instructions for your order..."
                  ></textarea>
                </div>
              </div>

              {/* Order Summary */}
              <div className="form-section order-summary-section">
                <h3>Order Summary</h3>
                <div className="order-summary-content">
                  <div className="order-items">
                    {cart.map(item => (
                      <div key={item.id} className="order-item">
                        <img 
                          src={item.labelImage || item.image} 
                          alt={item.name}
                          className="order-item-image"
                        />
                        <div className="order-item-info">
                          <h4>{item.name}</h4>
                          <p>Qty: {item.quantity}</p>
                        </div>
                        <div className="order-item-price">
                          ${(item.price * item.quantity).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="order-totals">
                    <div className="total-line">
                      <span>Subtotal ({getCartItemCount()} items):</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="total-line">
                      <span>Shipping:</span>
                      <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="total-line">
                      <span>Tax:</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="total-line total-final">
                      <span>Total:</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  {shipping === 0 && (
                    <div className="free-shipping-notice">
                      <i className="fas fa-truck"></i>
                      <span>FREE shipping on orders over $50!</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="checkout-actions">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting || cart.length === 0}
                  icon={isSubmitting ? 
                    <i className="fas fa-spinner fa-spin"></i> : 
                    <i className="fas fa-lock"></i>
                  }
                  className="place-order-btn"
                >
                  {isSubmitting ? 'Processing Order...' : `Place Order - $${total.toFixed(2)}`}
                </Button>

                <p className="checkout-notice">
                  <i className="fas fa-shield-alt"></i>
                  Your payment information is secure and encrypted
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;